import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Student } from '../../types';
import { PaginatedResponse } from '../../services/studentService';
import studentService from '../../services/studentService';

interface StudentState {
  students: Student[];
  selectedStudent: Student | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
  paginatedStudents: Student[];
}

const initialState: StudentState = {
  students: [],
  selectedStudent: null,
  isLoading: false,
  error: null,
  pagination: null,
  paginatedStudents: [],
};

// Async thunks
export const fetchStudents = createAsyncThunk(
  'students/fetchStudents',
  async (_, { rejectWithValue }) => {
    try {
      const response = await studentService.getAllStudents();
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch students');
    }
  }
);

export const fetchStudentsPaginated = createAsyncThunk(
  'students/fetchStudentsPaginated',
  async ({ page, size, search }: { page: number; size: number; search?: string }, { rejectWithValue }) => {
    try {
      const response = await studentService.getStudentsPaginated(page, size, search);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch students');
    }
  }
);

export const createStudent = createAsyncThunk(
  'students/createStudent',
  async (studentData: Partial<Student>, { rejectWithValue }) => {
    try {
      const response = await studentService.createStudent(studentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create student');
    }
  }
);

export const updateStudentAsync = createAsyncThunk(
  'students/updateStudent',
  async ({ id, studentData }: { id: string; studentData: Partial<Student> }, { rejectWithValue }) => {
    try {
      const response = await studentService.updateStudent(id, studentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update student');
    }
  }
);

export const deleteStudentAsync = createAsyncThunk(
  'students/deleteStudent',
  async (id: string, { rejectWithValue }) => {
    try {
      await studentService.deleteStudent(id);
      return id;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete student');
    }
  }
);

const studentSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    setStudents: (state, action: PayloadAction<Student[]>) => {
      state.students = action.payload;
    },
    setSelectedStudent: (state, action: PayloadAction<Student | null>) => {
      state.selectedStudent = action.payload;
    },
    addStudent: (state, action: PayloadAction<Student>) => {
      state.students.push(action.payload);
    },
    updateStudent: (state, action: PayloadAction<Student>) => {
      const index = state.students.findIndex(student => student.id === action.payload.id);
      if (index !== -1) {
        state.students[index] = action.payload;
      }
    },
    removeStudent: (state, action: PayloadAction<string>) => {
      state.students = state.students.filter(student => student.id !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearPagination: (state) => {
      state.pagination = null;
      state.paginatedStudents = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch students
      .addCase(fetchStudents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students = action.payload;
        state.error = null;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch students paginated
      .addCase(fetchStudentsPaginated.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudentsPaginated.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paginatedStudents = action.payload.content;
        state.pagination = action.payload.pagination;
        state.error = null;
      })
      .addCase(fetchStudentsPaginated.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create student
      .addCase(createStudent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students.push(action.payload);
        state.error = null;
      })
      .addCase(createStudent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update student
      .addCase(updateStudentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateStudentAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.students.findIndex(student => student.id === action.payload.id);
        if (index !== -1) {
          state.students[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateStudentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete student
      .addCase(deleteStudentAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteStudentAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students = state.students.filter(student => student.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteStudentAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setStudents,
  setSelectedStudent,
  addStudent,
  updateStudent,
  removeStudent,
  setLoading,
  setError,
  clearPagination,
} = studentSlice.actions;

export default studentSlice.reducer; 